import moment from 'moment';

import { getMonthAndYearPortfolioClosed } from './utils';

//Faixa aceitável de diários (dias úteis) dentro de um mês fechado
export const MIN_DIARYS_IN_MONTH = 18;
export const MAX_DIARYS_IN_MONTH = 30;

/**
 * Agrupa as linhas por uma chave e devolve apenas os grupos com mais de um registro,
 * ou seja, os registros duplicados. Cada duplicidade aparece uma única vez.
 *
 * @param {Array<Object>} rows
 * @param {string} key propriedade usada como chave do agrupamento (asset_id/account_id)
 * @returns {Array<Array<Object>>} grupos de registros duplicados
 */
export function groupDuplicatesByKey(rows, key) {

    const mapByKey = {};

    (rows || []).forEach(row => {

        const value = row ? row[key] : null;
        if (value === null || value === undefined) return;

        if (!mapByKey[value]) {
            mapByKey[value] = [];
        }
        mapByKey[value].push(row);
    });

    return Object.values(mapByKey).filter(group => group.length > 1);
}

/**
 * Pares de ativos DIFERENTES que possuem exatamente o mesmo saldo (diferente de zero),
 * o que costuma indicar um saldo replicado por engano.
 *
 * Ativos iguais são ignorados pois já são reportados como saldos duplicados.
 *
 * @param {Array<Object>} rows
 * @returns {Array<{a: Object, b: Object}>}
 */
export function findEqualBalancePairs(rows) {

    const list = rows || [];
    const pairs = [];
    const alreadyAdded = new Set();

    for (let i = 0; i < list.length; i++) {

        const balanceA = parseFloat(list[i].balance);
        if (!balanceA || isNaN(balanceA)) continue;

        for (let j = i + 1; j < list.length; j++) {

            if (list[i].asset_id === list[j].asset_id) continue;

            const balanceB = parseFloat(list[j].balance);
            if (balanceA !== balanceB) continue;

            //Um ativo com saldo duplicado geraria vários pares com o mesmo outro
            //ativo; o par de ativos só interessa uma vez
            const pairKey = [list[i].asset_id, list[j].asset_id].sort().join('-') + '@' + balanceA;
            if (alreadyAdded.has(pairKey)) continue;
            alreadyAdded.add(pairKey);

            pairs.push({ a: list[i], b: list[j] });
        }
    }

    return pairs;
}

/**
 * Meses do ano que não possuem NENHUM diário.
 *
 * Meses totalmente vazios não chegam a entrar no arrayRents, por isso não são
 * detectados pela checagem de quantidade de diários.
 *
 * O mês de início da carteira (e anteriores) é ignorado: a consulta de diários
 * filtra date_quota > portfolio_init, logo ele legitimamente não possui diários.
 * Meses posteriores ao último fechamento também são ignorados, pois não estão
 * faltando, apenas ainda não foram fechados.
 *
 * @param {Array<Object>} arrayRents retorno de loadClientDiaryByRange
 * @param {number} month mês selecionado na carteira
 * @param {number} year ano selecionado na carteira
 * @param {string|Date} portfolioInit data de início da carteira
 * @param {string} portfolioClosed último fechamento no formato 'MM/YYYY'
 * @returns {Array<number>} números dos meses sem diário
 */
export function findMissingDiaryMonths(arrayRents, month, year, portfolioInit, portfolioClosed) {

    if (!month || !year) return [];

    //monthYear vem sem zero à esquerda: '3/2025'
    const presentMonths = new Set((arrayRents || []).map(element => element.monthYear));

    let firstExpectedMonth = 1;
    if (portfolioInit) {

        const initDate = moment.utc(portfolioInit);
        if (initDate.year() > year) return [];

        //Espera-se diários apenas a partir do mês SEGUINTE ao início da carteira
        if (initDate.year() === year) {
            firstExpectedMonth = initDate.month() + 2;
        }
    }

    let lastExpectedMonth = month;
    const closed = getMonthAndYearPortfolioClosed(portfolioClosed);
    if (closed) {

        if (closed.year < year) return [];

        if (closed.year === year && closed.month < lastExpectedMonth) {
            lastExpectedMonth = closed.month;
        }
    }

    const missingMonths = [];
    for (let m = firstExpectedMonth; m <= lastExpectedMonth; m++) {
        if (!presentMonths.has(m + '/' + year)) {
            missingMonths.push(m);
        }
    }

    return missingMonths;
}

/**
 * Meses cuja quantidade de diários está fora da faixa esperada.
 *
 * @param {Array<Object>} arrayRents retorno de loadClientDiaryByRange
 * @returns {Array<{monthYear: string, month: number, year: number, count: number, rent: number}>}
 */
export function findInvalidDiaryCountMonths(arrayRents) {

    return (arrayRents || [])
        .filter(element => element.diarys
            && (element.diarys.length < MIN_DIARYS_IN_MONTH
                || element.diarys.length > MAX_DIARYS_IN_MONTH))
        .map(element => {

            const splitted = element.monthYear.split('/');

            return {
                monthYear: element.monthYear,
                month: parseInt(splitted[0]),
                year: parseInt(splitted[1]),
                count: element.diarys.length,
                rent: element.rent,
            }
        });
}

/**
 * Ativos de FUNDO que voltaram repetidos na consulta da carteira (mesmo asset id mais de uma vez).
 *
 * A consulta de ativos (fund.controller.getClientAssetListByDateAndRegime) faz LEFT JOIN em
 * fund_local_quotas e em rent_funds — nenhuma das duas tem UNIQUE na chave do join — e ainda
 * seleciona flq.id, então o DISTINCT não deduplica: uma linha duplicada em qualquer uma delas
 * devolve o ativo repetido. O agrupamento por fundo soma o saldo em dobro, o PL de abertura infla
 * e todo o descolamento contra a cota do mês anterior vira "rentabilidade" do 1º dia do mês.
 *
 * Dois detalhes do formato de assetsFormatted que a checagem precisa respeitar:
 *  - quando um fundo tem mais de uma conta, o próprio pai é empurrado como PRIMEIRO item de
 *    subAssets (organizeAssetsAndTransactions), logo o id do pai sempre reaparece ali. Havendo
 *    subAssets, só eles contam — o pai é um agregado sintético;
 *  - títulos ficam na mesma lista (distinguidos por title_id), mas agrupam em subAssetsTitles sem
 *    repetir o pai e vêm de asset_titles, cujos ids colidiriam com os de assets. Ficam de fora:
 *    a duplicidade tratada aqui é a do lado dos fundos.
 *
 * @param {Array<Object>} assets assetsFormatted
 * @returns {Array<Array<Object>>} grupos de ativos repetidos
 */
export function findDuplicatedPortfolioAssets(assets) {

    const flat = [];

    (assets || []).filter(asset => asset && !asset.title_id).forEach(asset => {

        const rows = asset.subAssets && asset.subAssets.length ? asset.subAssets : [asset];

        rows.forEach(row => {

            if (!row) return;

            flat.push({
                asset_id: row.id,
                fund_name: row.fund_name,
                cnpj: row.cnpj,
                number_account: row.number_account,
            });
        });
    });

    return groupDuplicatesByKey(flat, 'asset_id');
}

/**
 * Dias do diário da carteira com valor não-finito (NaN/Infinity) em `pl` ou `newPl`.
 *
 * NaN é absorvente numa soma: basta um ativo sem cota no dia para o total do dia sair NaN. O
 * JSON.stringify converte NaN em null, o INSERT grava NULL em client_rents.new_pl e a tela de
 * retornos mostra "R$ NaN" — no dia e também no dia seguinte, cujo PL de abertura é o new_pl do
 * anterior. Como client_rents nunca sofre UPDATE (só reset e reinserção), é melhor bloquear
 * antes de gravar.
 *
 * @param {Object} diaries mapa rótulo > diário ({ 'YYYY-MM-DD': { pl, newPl } })
 * @returns {Array<{diary: string, date: string, field: string}>}
 */
export function findInvalidDiaryDays(diaries) {

    const invalids = [];

    Object.entries(diaries || {}).forEach(([diaryName, diary]) => {

        if (!diary || typeof diary !== 'object') return;

        Object.entries(diary).forEach(([date, row]) => {

            if (!row) return;

            ['pl', 'newPl'].forEach(field => {

                if (!Number.isFinite(row[field])) {
                    invalids.push({ diary: diaryName, date: date, field: field });
                }
            });
        });
    });

    return invalids;
}

/**
 * Total de inconsistências encontradas na análise.
 *
 * @param {Object} analysis
 * @returns {number}
 */
export function countAnalysisIssues(analysis) {

    if (!analysis) return 0;

    return (analysis.missingDiaryMonths?.length ?? 0)
        + (analysis.invalidDiaryCountMonths?.length ?? 0)
        + (analysis.duplicatedCurrentBalances?.length ?? 0)
        + (analysis.duplicatedNextBalances?.length ?? 0)
        + (analysis.duplicatedAccountBalances?.length ?? 0)
        + (analysis.equalBalancePairs?.length ?? 0)
        + (analysis.duplicatedLocalQuotas?.length ?? 0);
}
